import { useEffect, useState } from 'react';
import app from '../firebase/config';
import { decodeFields } from './canonical';
import { takePublicConfigRead } from './giving';
import { storedArrival } from './campaign';
import { countingVisitorId, isInternalBrowser, trackXp, onHiding } from './analytics';
import { onTeam, subscribeTeamVoice } from './teamVoice';
import {
  cleanSwitches, frontDoorChoice, firstAnswerMeta, keyFor, firstAnswerStep, frontDoorAskStep,
} from './experimentsCore';

/**
 * The two A/B tests in the page (docs/plans/experiments.md; the pure parts are experimentsCore.js).
 *
 * The switches. The browser can't read meta/limits, so scripts/set-limits.js writes a copy of the
 * four switches into meta/publicConfig. This file reads that document as the page opens, in
 * Firestore's plain REST form (one GET, no sign-in: it is public, like meta/canonicalAnswers), and
 * hands the same read to utils/giving.js, so it is still the page's one read of it. What it said
 * is kept in localStorage (apr-xp), for the next page's first paint: a returning visitor's sign-in
 * comes back from the browser at once, sooner than any read. Every page's own read replaces it, so
 * a changed switch reaches a returning browser one page late at most (kept 30 days).
 *
 * Front Door decides once a page, at the welcome's first paint (frontDoorFor): from this page's
 * read when it is back by then (a first visit's sign-in takes longer than the read), else the kept
 * copy, else not at all (today's welcome, uncounted; the same for both groups). A visit keeps its
 * welcome: a reload in the same tab shows the same one.
 * It counts the visit when the welcome's questions come in, or would have in Bare (ChatView), and
 * then the visit's first question (ask) and its first in their own words (typed), once each
 * (sessionStorage apr-xp-fd).
 *
 * First Answer is the server's to decide. The page sends its visitor id with the first question of
 * a conversation in the main chat (firstAnswerFields), only while the copy says the test is on and
 * the browser counts visits, never from a browser marked as the team's or a landing's tab; a team
 * member's own pick goes as xpPick instead. The answer's metadata says { k, arm, short }; the page
 * shows More under a short answer. Its first words enrol the conversation in this tab
 * (sessionStorage apr-xp-fa, by conversation id), and only this tab counts what follows, once each:
 * More, a follow-up, going on, Go deeper, Ask the real Rob, and leaving within 15 seconds of the
 * answer's end (watchFirstAnswerLeaving). A Stop or a reload in the tab keeps the record; another
 * tab, or the conversation coming back later from the draft, counts nothing more (the measurement
 * review, Oct 21).
 *
 * Nothing here ever shows anyone their group, and nothing it sends says what anyone typed.
 */

const CACHE_KEY = 'apr-xp';
const CACHE_MS = 30 * 24 * 3600 * 1000;
const FD_KEY = 'apr-xp-fd';
const FA_KEY = 'apr-xp-fa';
const TEAM_PICK_KEY = 'apr-team-fa';
const READ_TIMEOUT_MS = 6000;
// Leaving is measured over the same window for both groups: 15 seconds from the moment the answer
// has finished coming in (the stream's done event), never from its first words. A full answer takes
// about 12 seconds longer to arrive than a short one, so a window from the first words held the
// whole of Full's stream and a second or two of Short + More's (the measurement review, Oct 21).
export const LEFT_WINDOW_MS = 15000;
// A tap on the answer itself (a source or a Scripture passage, which open elsewhere; Listen, Share,
// Copy) often hides the page for a moment: a hide within 2 seconds of one is not leaving.
export const TAP_GRACE_MS = 2000;
export const ANSWER_TAPS = 'sup.cv-cite a, .cv-cited a, .cv-src, .cv-scripture, .cv-action.listen, .cv-action.share, .cv-action.copy';

export const FA_PICKS = ['full', 'short'];
export const FA_PICK_NAMES = { full: 'Full', short: 'Short + More' };

let fresh = null; // this page's read, once it is back: { fa, fd }
let started = false;
let frontDoorMemo; // undefined: not decided yet this page

function readJson(store, key) {
  try { return JSON.parse(window[store].getItem(key) || 'null'); } catch (e) { return null; }
}

function writeJson(store, key, value) {
  try { window[store].setItem(key, JSON.stringify(value)); } catch (e) { /* storage blocked: this page only */ }
}

function projectId() {
  try {
    return (app && app.options && app.options.projectId) || 'askpastorrob-b6812';
  } catch (e) {
    return 'askpastorrob-b6812';
  }
}

/** meta/publicConfig's fields as plain values ({} when there is none); throws when it can't be read. */
async function readPublicConfig() {
  const url = `https://firestore.googleapis.com/v1/projects/${encodeURIComponent(projectId())}/databases/(default)/documents/meta/publicConfig`;
  const signal = typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(READ_TIMEOUT_MS) : undefined;
  const res = await fetch(url, { signal, cache: 'no-store', credentials: 'omit' });
  if (res.status === 404) return {};
  if (!res.ok) throw new Error(`meta/publicConfig: HTTP ${res.status}`);
  const body = await res.json();
  return decodeFields((body && body.fields) || {});
}

/**
 * Starts this page's read of the public settings: call once, as the page opens, after
 * startAnalytics (src/index.js). On the dev server (localhost, the emulators) giving.js reads them
 * the usual way, and neither test runs.
 */
export function startExperiments() {
  if (started || typeof window === 'undefined') return;
  started = true;
  if (window.location.hostname === 'localhost') return;
  let read;
  try {
    read = readPublicConfig();
  } catch (e) {
    return;
  }
  takePublicConfigRead(read);
  read.then((doc) => {
    fresh = cleanSwitches(doc);
    writeJson('localStorage', CACHE_KEY, { s: fresh, at: Date.now() });
  }, () => { /* unread: the kept copy, or neither test, for this page */ });
}

/** The switches as this page knows them: its own read, else the copy the last page kept (30 days at most), else null. */
export function switchesNow() {
  if (fresh) return fresh;
  const kept = readJson('localStorage', CACHE_KEY);
  if (kept && typeof kept.at === 'number' && Date.now() - kept.at < CACHE_MS && kept.s) return cleanSwitches({
    xpFirstAnswer: kept.s.fa && kept.s.fa.mode, xpFirstAnswerRun: kept.s.fa && kept.s.fa.run,
    xpFrontDoor: kept.s.fd && kept.s.fd.mode, xpFrontDoorRun: kept.s.fd && kept.s.fd.run,
  });
  return null;
}

// ---------------------------------------------------------------------------
// Front Door
// ---------------------------------------------------------------------------

/**
 * This page's Front Door, decided the first time the welcome asks (its first paint), and kept for
 * the page: { key, arm, bare }, or null (not in the test: today's welcome). A visit that already
 * saw its welcome keeps it, unless the switches now say the test moved on.
 */
export function frontDoorFor({ guest = false } = {}) {
  if (frontDoorMemo !== undefined) return frontDoorMemo;
  let choice = null;
  try {
    const switches = switchesNow();
    const seen = readJson('sessionStorage', FD_KEY);
    const stillOn = !switches || (seen && keyFor('fd', switches.fd.mode, switches.fd.run) === seen.k);
    if (seen && seen.k && (seen.arm === 'A' || seen.arm === 'B') && stillOn) {
      choice = { key: seen.k, arm: seen.arm, bare: seen.bare === true };
    } else {
      choice = frontDoorChoice({
        switches, vid: countingVisitorId(), guest, internal: isInternalBrowser(), landing: Boolean(storedArrival().landing),
      });
    }
  } catch (e) {
    choice = null;
  }
  frontDoorMemo = choice;
  return choice;
}

/** The welcome's questions came in, or would have in Bare: the visit is counted in its group, once. */
export function noteFrontDoorShown(choice) {
  if (!choice || !choice.key) return;
  const seen = readJson('sessionStorage', FD_KEY);
  if (seen && seen.k === choice.key && seen.enr) return;
  writeJson('sessionStorage', FD_KEY, { k: choice.key, arm: choice.arm, bare: choice.bare === true, enr: true });
  trackXp({ x: 'fd', k: choice.key, arm: choice.arm, m: 'enr' });
}

/**
 * A question was asked in the main chat (ChatView): in a visit Front Door counted, its first
 * question (ask) and its first in their own words (typed: typed or spoken, not a tap on a question
 * to start with), once each.
 */
export function noteOwnQuestion({ typed = false } = {}) {
  const { seen, events } = frontDoorAskStep(readJson('sessionStorage', FD_KEY), { typed });
  if (!events.length) return;
  writeJson('sessionStorage', FD_KEY, seen);
  events.forEach((m) => trackXp({ x: 'fd', k: seen.k, arm: seen.arm, m }));
}

// ---------------------------------------------------------------------------
// First Answer
// ---------------------------------------------------------------------------

/** The team's own pick for their first answers: 'full' (the default) or 'short'; null for anyone else. */
export function teamFirstAnswerPick() {
  if (!onTeam()) return null;
  let saved = null;
  try { saved = window.localStorage.getItem(TEAM_PICK_KEY); } catch (e) { /* blocked: the default */ }
  return FA_PICKS.includes(saved) ? saved : 'full';
}

const pickListeners = new Set();

export function setTeamFirstAnswerPick(pick) {
  if (!FA_PICKS.includes(pick)) return;
  try { window.localStorage.setItem(TEAM_PICK_KEY, pick); } catch (e) { /* blocked: nothing kept */ }
  pickListeners.forEach((fn) => { try { fn(teamFirstAnswerPick()); } catch (e) { /* never breaks the pick */ } });
}

/** The pick for a component: null for everyone getMyRole did not name. */
export function useTeamFirstAnswerPick() {
  const [pick, setPick] = useState(teamFirstAnswerPick);
  useEffect(() => {
    const update = () => setPick(teamFirstAnswerPick());
    update();
    pickListeners.add(update);
    const off = subscribeTeamVoice(update); // the role arriving, or going
    return () => { pickListeners.delete(update); off(); };
  }, []);
  return pick;
}

/**
 * What a question carries for First Answer: only the first of a conversation in the main chat.
 * The visitor id while the copy says the test is on and this browser counts visits (never the
 * team's role, a browser marked internal, or a landing's tab); a team member's pick of Short + More.
 */
export function firstAnswerFields({ mode, first }) {
  if (mode !== 'archive' || !first) return {};
  try {
    if (onTeam()) return teamFirstAnswerPick() === 'short' ? { xpPick: 'short' } : {};
    const switches = switchesNow();
    if (!switches || !switches.fa.mode || isInternalBrowser() || storedArrival().landing) return {};
    const xv = countingVisitorId();
    return xv ? { xv } : {};
  } catch (e) {
    return {};
  }
}

/** First Answer's word in an answer's metadata, made safe (experimentsCore.firstAnswerMeta). */
export function readFirstAnswer(raw) {
  return firstAnswerMeta(raw);
}

function faRecords() {
  const all = readJson('sessionStorage', FA_KEY);
  return all && typeof all === 'object' && !Array.isArray(all) ? all : {};
}

function keepFaRecords(all) {
  // The last 20 conversations are plenty: a tab rarely has more than a few.
  const ids = Object.keys(all).sort((x, y) => (Number(all[x] && all[x].at) || 0) - (Number(all[y] && all[y].at) || 0)).slice(-20);
  writeJson('sessionStorage', FA_KEY, Object.fromEntries(ids.map((id) => [id, all[id]])));
}

/**
 * A first answer in the test reached its first words (ChatView): the conversation is enrolled in
 * this tab, under its group, and `enr` is counted. Once a conversation; returns whether it was now.
 */
export function enrollFirstAnswer(conversationId, fa) {
  if (!fa || !fa.k || (fa.arm !== 'A' && fa.arm !== 'B') || typeof conversationId !== 'string' || !conversationId) return false;
  const all = faRecords();
  if (all[conversationId]) return false;
  all[conversationId] = { k: fa.k, arm: fa.arm, sent: ['enr'], at: Date.now() };
  keepFaRecords(all);
  trackXp({ x: 'fa', k: fa.k, arm: fa.arm, m: 'enr' });
  return true;
}

/** Whether this tab enrolled the conversation in First Answer. */
export function firstAnswerEnrolled(conversationId) {
  return Boolean(typeof conversationId === 'string' && conversationId && faRecords()[conversationId]);
}

/**
 * One moment after a first answer this tab enrolled (experimentsCore.firstAnswerStep): More, a
 * follow-up, Go deeper, Ask the real Rob, or leaving. Each is counted once; nothing for a
 * conversation this tab never enrolled. Returns whether anything was counted now.
 */
export function noteFirstAnswer(conversationId, metric) {
  if (typeof conversationId !== 'string' || !conversationId) return false;
  const all = faRecords();
  const { record, events } = firstAnswerStep(all[conversationId], metric);
  if (!events.length) return false;
  all[conversationId] = record;
  keepFaRecords(all);
  events.forEach((m) => trackXp({ x: 'fa', k: record.k, arm: record.arm, m }));
  return true;
}

/**
 * Leaving: called when a first answer has finished coming in (ChatView, on the stream's done
 * event), it watches for 15 seconds, the same for both groups. The page hidden or closed in that
 * time counts as `left`, once; not when it hides within 2 seconds of a tap on the answer itself
 * (`taps`: a source, a Scripture passage, Listen, Share, Copy), which is reading on, not leaving,
 * and ends the watch. Going on (More, a follow-up, Go deeper, Ask the real Rob) ends it too, and
 * firstAnswerStep never counts leaving after going on. Nothing is counted when the answer never
 * finished (Stop, an error): there is no end to measure from. Returns { cancel() }.
 */
export function watchFirstAnswerLeaving(conversationId, { taps = ANSWER_TAPS } = {}) {
  let over = false;
  let timer = null;
  let offHide = null;
  let tappedAt = -Infinity;
  const stop = () => {
    if (over) return;
    over = true;
    clearTimeout(timer);
    if (offHide) offHide();
    if (typeof document !== 'undefined') {
      document.removeEventListener('pointerdown', onTap, true);
      document.removeEventListener('keydown', onKey, true);
    }
  };
  const tapped = (el) => Boolean(el && el.closest && el.closest(taps));
  function onTap(e) {
    if (tapped(e && e.target)) tappedAt = Date.now();
  }
  function onKey(e) {
    // The keyboard's tap: Enter or Space on a focused source, passage or action.
    if (e && (e.key === 'Enter' || e.key === ' ') && tapped(document.activeElement)) tappedAt = Date.now();
  }
  offHide = onHiding(() => {
    if (over) return;
    if (Date.now() - tappedAt >= TAP_GRACE_MS) noteFirstAnswer(conversationId, 'left');
    stop();
  });
  if (typeof document !== 'undefined') {
    document.addEventListener('pointerdown', onTap, true);
    document.addEventListener('keydown', onKey, true);
  }
  timer = setTimeout(stop, LEFT_WINDOW_MS);
  return { cancel: stop };
}
